import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Select,
  message,
  Spin,
  Checkbox,
  Input,
  Space,
  Pagination,
  Empty
} from 'antd';
import {
  SearchOutlined,
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons';
import {
  assign_category_to_products,
  get_all_products_without_category
} from '../../../../Controllers/Products/Products';
import { get_all_product_categories } from '../../../../Controllers/Products/Categories';
import toast from 'react-hot-toast';

const { Option } = Select;

const AssignCategoryModal = ({
  open,
  onClose,
  selectedProductIds = [],
  onSuccess,
  productData = {},
  cat_Id
}) => {

  console.log("selected", selectedProductIds)

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState(selectedProductIds);
  const [searchText, setSearchText] = useState('');
  const [selectAllCurrentPage, setSelectAllCurrentPage] = useState(false);
  const [selectAllGlobal, setSelectAllGlobal] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
    limit: 500
  });

  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchCategories = async () => {
    try {
      setCategoryLoading(true);
      const result = await get_all_product_categories({
        status: 1,
        page: 1
      });

      if (result?.status) {
        setCategories(result?.data?.data || []);
      } else {
        toast.error(result?.message || 'Failed to load categories');
      }
    } catch (error) {
      toast.error(error.message || 'Error loading categories');
    } finally {
      setCategoryLoading(false);
    }
  };


  const fetchProducts = async (page = 1, search = '', isSearch = false) => {
    try {
      setProductLoading(true);

      const result = await get_all_products_without_category({
        search: search,
        page: page,
        limit: pagination.limit
      });

      if (result?.status) {
        const productList = result?.data?.data || [];

        if (isSearch) {
          const existingProductIds = productList.map(p => p._id);
          setSelectedProducts(prev =>
            prev.filter(id => existingProductIds.includes(id) || !productList.some(p => p._id === id))
          );
        }

        setProducts(productList);

        setPagination(prev => ({
          ...prev,
          currentPage: result?.data?.currentPage || page,
          totalCount: result?.data?.totalCount || 0,
          totalPages: result?.data?.totalPages || 0
        }));

        if (selectedProductIds.length > 0 && productData._id && page === 1) {
          setSelectedProducts(prev => {
            const newSelections = [...prev];
            selectedProductIds.forEach(id => {
              if (!newSelections.includes(id)) {
                newSelections.push(id);
              }
            });
            return newSelections;
          });
        }
      } else {
        toast.error(result?.message || 'Failed to load products');
      }
    } catch (error) {
      toast.error(error.message || 'Error loading products');
    } finally {
      setProductLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchProducts(1, value, true);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handlePageChange = (page) => {
    fetchProducts(page, searchText);
    setSelectAllCurrentPage(false);
  };

  const handleProductSelect = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAllCurrentPage = () => {
    const currentPageProductIds = products.map(product => product._id);

    if (selectAllCurrentPage) {
      setSelectedProducts(prev =>
        prev.filter(id => !currentPageProductIds.includes(id))
      );
    } else {
      const newSelections = [...selectedProducts];
      currentPageProductIds.forEach(id => {
        if (!newSelections.includes(id)) {
          newSelections.push(id);
        }
      });
      setSelectedProducts(newSelections);
    }
    setSelectAllCurrentPage(!selectAllCurrentPage);
  };

  const handleSelectAllGlobal = async () => {
    if (selectAllGlobal) {
      setSelectedProducts([]);
      setSelectAllGlobal(false);
    } else {
      try {
        setLoading(true);
        toast.loading('Fetching all products...', { id: 'fetch-all' });

        const countResult = await get_all_products_without_category({
          search: searchText,
          page: 1,
          limit: 1
        });

        if (countResult?.status) {
          const totalCount = countResult?.data?.totalCount || 0;

          if (totalCount > 1000) {
            Modal.confirm({
              title: `Select All ${totalCount.toLocaleString()} Products?`,
              content: `You are about to select ${totalCount.toLocaleString()} products. This may take a moment.`,
              okText: 'Continue',
              cancelText: 'Cancel',
              onOk: async () => {
                // Fetch all product IDs
                const allResult = await get_all_products_without_category({
                  search: searchText,
                  page: 1,
                  limit: totalCount
                });

                if (allResult?.status) {
                  const allProductIds = allResult.data.data.map(p => p._id);
                  setSelectedProducts(allProductIds);
                  setSelectAllGlobal(true);
                  toast.success(`Selected ${allProductIds.length} products`, { id: 'fetch-all' });
                }
              }
            });
          } else {
            const allResult = await get_all_products_without_category({
              search: searchText,
              page: 1,
              limit: totalCount
            });

            if (allResult?.status) {
              const allProductIds = allResult.data.data.map(p => p._id);
              setSelectedProducts(allProductIds);
              setSelectAllGlobal(true);
              toast.success(`Selected ${allProductIds.length} products`, { id: 'fetch-all' });
            }
          }
        }
      } catch (error) {
        toast.error('Failed to select all products', { id: 'fetch-all' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAssignCategory = async () => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    try {
      setLoading(true);

      const data = {
        category_id: selectedCategory,
        product_ids: selectedProducts
      };

      const result = await assign_category_to_products(data);

      if (result?.status) {
        toast.success(result.message || 'Category assigned successfully');
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        toast.error(result?.message || 'Failed to assign category');
      }
    } catch (error) {
      toast.error(error.message || 'Error assigning category');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
      fetchProducts(1);

      if (cat_Id) {
        setSelectedCategory(cat_Id);
      } else if (productData?.category?._id) {
        setSelectedCategory(productData.category._id);
      }
    } else {
      setSelectedProducts(selectedProductIds);
      setSearchText('');
      setSelectAllCurrentPage(false);
      setSelectAllGlobal(false);
      setPagination({
        currentPage: 1,
        totalCount: 0,
        totalPages: 0,
        limit: 50
      });
    }
  }, [open]);

  useEffect(() => {
    if (products.length > 0) {
      const currentPageProductIds = products.map(p => p._id);
      const allSelectedOnPage = currentPageProductIds.every(id =>
        selectedProducts.includes(id)
      );
      setSelectAllCurrentPage(allSelectedOnPage);

      if (selectedProducts.length === pagination.totalCount && pagination.totalCount > 0) {
        setSelectAllGlobal(true);
      } else {
        setSelectAllGlobal(false);
      }
    } else {
      setSelectAllCurrentPage(false);
      setSelectAllGlobal(false);
    }
  }, [selectedProducts, products, pagination.totalCount]);

  return (
    <Modal
      title="Assign Category to Products"
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleAssignCategory}
          disabled={!selectedCategory || selectedProducts.length === 0}
          className="!bg-[#0dacce] !border-[#0dacce] hover:!bg-[#0b9abb] hover:!border-[#0b9abb] text-white"

        >
          Assign Category ({selectedProducts.length.toLocaleString()} products)
        </Button>,
      ]}
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Category *
          </label>
          <Select
            placeholder="Search and select category"
            value={selectedCategory}
            onChange={setSelectedCategory}
            style={{ width: '100%' }}
            showSearch
            allowClear
            loading={categoryLoading}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {categories.map(category => (
              <Option key={category._id} value={category._id}>
                {category.name}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Select Products
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({selectedProducts.length.toLocaleString()} selected of {pagination.totalCount.toLocaleString()} total)
                </span>
              </label>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Checkbox
                  checked={selectAllCurrentPage}
                  onChange={handleSelectAllCurrentPage}
                  disabled={products.length === 0}
                >
                  <span className="text-sm">Select Page ({products.length})</span>
                </Checkbox>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={selectAllGlobal}
                  onChange={handleSelectAllGlobal}
                  disabled={pagination.totalCount === 0}
                >
                  <span className="text-sm">Select All ({pagination.totalCount.toLocaleString()})</span>
                </Checkbox>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <Input
              placeholder="Search products by name or SKU..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              size="large"
              className="!border-[#0dacce] hover:!border-[#0dacce] focus:!border-[#0dacce]"
            />
          </div>

          <div className="border rounded-lg">
            <div className="flex justify-between items-center p-3 bg-gray-50 border-b">
              <div className="text-sm text-gray-600">
                Showing {(pagination.currentPage - 1) * pagination.limit + 1}-
                {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                of {pagination.totalCount.toLocaleString()} products
              </div>
              {pagination.totalPages > 1 && (
                <div className="flex items-center">
                  <Pagination
                    current={pagination.currentPage}
                    total={pagination.totalCount}
                    pageSize={pagination.limit}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showQuickJumper
                    size="small"
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </div>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {productLoading ? (
                <div className="p-8 text-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24, color: '#0dacce' }} spin />}
                    size="large"
                  />
                  <p className="text-gray-500 mt-4">Loading products...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="p-8 text-center">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      searchText
                        ? `No products found matching "${searchText}"`
                        : "No products found without category"
                    }
                  />
                </div>
              ) : (
                <div className="divide-y">
                  {products.map(product => (
                    <div
                      key={product._id}
                      className={`p-3 hover:bg-gray-50 cursor-pointer flex items-center ${selectedProducts.includes(product._id) ? 'bg-green-50 border-l-4 border-[#0dacce]' : ''
                        }`}
                      onClick={() => handleProductSelect(product._id)}
                    >
                      <Checkbox
                        checked={selectedProducts.includes(product._id)}
                        onChange={() => handleProductSelect(product._id)}
                        className="mr-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {product.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <span className="mr-4">SKU: {product.ref || 'N/A'}</span>
                          <span className="mr-4">Stock: {product.stock?.toFixed?.(2) || 0}</span>
                          {product.brand && (
                            <span>Brand: {product.brand.name}</span>
                          )}
                        </div>
                      </div>
                      {product.category && (
                        <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          Has category
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex justify-between items-center p-3 bg-gray-50 border-t">
                <div className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="small"
                    type='primary'
                    icon={<LeftOutlined />}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="!bg-[#0dacce] !border-[#0dacce] hover:!bg-[#0b9abb] hover:!border-[#0b9abb] text-white"
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    type='primary'
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="!bg-[#0dacce] !border-[#0dacce] hover:!bg-[#0b9abb] hover:!border-[#0b9abb] text-white"
                  >
                    Next
                    <RightOutlined />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedProducts.length > 0 && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium text-green-800 mb-1">Summary</h4>
                <p className="text-green-700">
                  <strong>{selectedProducts.length.toLocaleString()}</strong> product(s) selected
                </p>
                {pagination.totalCount > 0 && (
                  <p className="text-sm text-blue-600 mt-1">
                    ({((selectedProducts.length / pagination.totalCount) * 100).toFixed(1)}% of all products without category)
                  </p>
                )}
              </div>
              {selectedProducts.length > 500 && (
                <div className="text-orange-600 bg-orange-50 px-3 py-1 rounded">
                  <strong>Note:</strong> Bulk operation in progress
                </div>
              )}
            </div>
          </div>
        )}

        {selectedProducts.length > 1000 && (
          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
            <div className="flex items-start">
              <ExclamationCircleFilled className="text-yellow-500 mr-2 mt-0.5" />
              <div className="text-yellow-800">
                <p className="font-medium">Large Selection Detected</p>
                <p className="text-sm">
                  You have selected {selectedProducts.length.toLocaleString()} products.
                  This operation may take a few moments to complete.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AssignCategoryModal;